<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent" v-if="component"></column-header>
    <div>
      <div class="row">
				<div class="col-md-12">
          <h3 v-if="page.name">{{ page.name }}</h3>
          <h3 v-else>{{ _('New page') }}</h3>
        </div>
      </div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <editor-tabs :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-xs-12">
                <h3>{{ _('General page information') }}</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6 leftcol">
                <div class="form-group languageversioned">
                  <label for="name">{{ _('Name') }}</label>
                  <input type="text" class="form-control" id="pageTitle" :placeholder="_('Enter page name')" name="name" v-model="page.name" />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <div class="form-group languageversioned">
                  <label for="description">{{ _('Short description') }}</label>
                  <editor v-model="page.description" />
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('images')" class="tab-pane active" id="images" v-if="isTabActive('images')" key="images">
            <div class="row">
              <div class="col-lg-6 leftcol">
                <h3>{{ _('Main image') }}</h3>
                <image-slot :file-id="page.file_id" :ext="page.file_ext" :owner-key="uploadOwnerKey" upload-slot="main" input-id="mainImage" image-element-id="pageImage" remove-button-id="removeImageButton" v-on:upload="queueUploads($event, 'main')" v-on:library="chooseFromLibrary('main')" v-on:remove="removeImage('main')" v-on:details="editCaption('main')" />
              </div>
              <div class="col-lg-6 rightcol">
                <h3>{{ _('Additional images') }}</h3>
                <image-gallery id="additionalImages" v-if="page" v-model:images="page.sharedFile" :owner-key="uploadOwnerKey" upload-slot="gallery" :empty-text="_('This page has no additional images.')" input-id="additionalImage" v-on:upload="queueUploads($event, 'gallery')" v-on:library="chooseFromLibrary('gallery', { multiple: true, max: $event })" v-on:remove="removeImage($event)" v-on:details="editCaption($event)" />
                <div class="row">
                  <div class="col-xs-12">
                    <hr/>
                    <p>{{ _('The main image is used in the embeddable widget and in Johku storefront. This image is also used in channels that enable setting a main image. The page may have a maximum of twenty images in addition to the main image.') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('saleschannels')" class="tab-pane active" id="saleschannels" v-if="isTabActive('saleschannels')" key="saleschannels">
            <div class="row">
              <div class="col-xs-12">
                <div class="panel-group" id="accordion" role="tablist" aria-multiselectable="true">
                  <div class="panel panel-default">
                    <div class="panel-heading" role="tab" id="headingOne">
                      <h4 class="panel-title">
                        <a role="button" data-toggle="collapse" data-parent="#accordion" href="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                          {{ _('Enabled Sales Channels for this Page') }}
                        </a>
                      </h4>
                    </div>
                    <div id="collapseOne" class="panel-collapse collapse in" role="tabpanel" aria-labelledby="headingOne">
                      <div class="panel-body">
                        <div class="row">
                          <div class="col-sm-6 panel-column">
                            <h4>{{ _('Johku Storefront') }}</h4>
                            <div class="checkbox">
                              <label>
                                <input type="checkbox" class="globalCatalogCheckbox" name="enable_catalog[]" v-model.number="enableInCatalog['global']" value="1" v-on:input="enableInCatalogChanged('global')" />
                                {{ _('Include in product catalog') }}
                              </label>
                            </div>
                            <div class="checkbox" v-for="version in versions">
                              <label>
                                <input type="checkbox" class="versionCatalogCheckbox" name="enable_catalog[]" v-model.number="enableInCatalog[version.identifier]" value="1" v-on:input="enableInCatalogChanged(version.identifier)" />
                                {{ _('Include in product catalog') }}: {{ version.name }}
                              </label>
                            </div>
                            <div class="privatechannels" v-if="page.privateSaleschannels && page.privateSaleschannels.length">
                              <div class="checkbox" v-for="privateSaleschannel in page.privateSaleschannels">
                                <label>
                                  <input type="checkbox" :value="privateSaleschannel" v-model="page.sharedSaleschannel" :key="privateSaleschannel.id" />
                                  {{ privateSaleschannel.name }}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
      </div>
    </nav>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'
import uploadaware from '~/lib/uploadaware'
import { Editor, FileCaption } from '#components'

export default {
  data () {
    return {
      page: {}
    }
  },
  mixins: [components, uploadaware],
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'images', label: this._('Images') },
        { id: 'saleschannels', label: this._('Sales Channels') }
      ])
    },
    isNewPage () {
      return !this.page || !this.page.id
    },
    uploadOwner () {
      if (!this.page) {
        return null
      }
      const id = this.page.id
      const label = this._('Page') + (id ? ' ' + id : '') + (this.page.name ? ': ' + this.page.name : '')
      return {
        type: 'page',
        id,
        label,
        open: {
          component: 'PageDetails',
          title: label,
          width: 6,
          data: {
            pageId: id,
            activeTab: 'images'
          }
        }
      }
    },
    enableInCatalog () {
      let ret = {}
      let enabled = []
      if (this.page.enable_catalog) {
        if (this.page.enable_catalog.length) {
          enabled = this.page.enable_catalog
        } else if (this.page.enable_catalog.split) {
          enabled = this.page.enable_catalog.split(';')
        }
      }
      ret['global'] = enabled[0] === '1' ? 1 : 0
      for (let i = 0; i < this.allVersions.length; i++) {
        ret[this.allVersions[i].identifier] = enabled.includes(this.allVersions[i].identifier) ? 1 : (ret['global'] ? 1 : 0)
      }
      return ret
    },
    versions () {
      const enabledVersions = this.settings.versions.split(';')
      let ret = []
      for (let i = 0; i < this.allVersions.length; i++) {
        if (enabledVersions.includes(this.allVersions[i].identifier)) {
          ret.push(this.allVersions[i])
        }
      }
      return ret
    },
    myallocatorIframeUrl () {
      return 'about:blank'
    }
  },
  watch: {
    myAllocatorLinks: {
      deep: true,
      handler (newLinks) {
        for (let i = 0; i < this.myAllocatorRooms.length; i++) {
          for (let prodId in newLinks) {
            if (Object.prototype.hasOwnProperty.call(newLinks, prodId)) {
              if (parseInt(this.myAllocatorRooms[i].id, 10) === parseInt(newLinks[prodId], 10)) {
                this.myAllocatorRooms[i].product = parseInt(prodId, 10)
              }
            }
          }
        }
      }
    }
  },
  methods: {
    isPriorityCountry (id) {
      return ['SE', 'NO', 'DK', 'EE', 'RU'].indexOf(id) !== -1
    },
    async reloadOnVersionChange () {
      if (!this.page || !this.page.id) {
        return
      }
      await this.loadPage(this.page.id)
    },
    loadPage (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/pages/' + id + '.json?channels=true&apikey=' + this.apiKey + this.versionStr)
        } catch (e) {
          this.showError(e)
          reject()
          this.loading = false
          return
        }
        this.page = response.data
        this.storeState()
        this.loading = false
        this.applyPendingUploads()
        resolve(this.page)
      })
    },
    enableInCatalogChanged (version) {
      if (version === 'global') {
        if (this.page.enable_catalog === '1' || this.page.enable_catalog[0] === '1') {
          this.page.enable_catalog = ''
        } else {
          this.page.enable_catalog = ['1']
        }
      } else {
        var versions = []
        const enabledVersions = this.settings.versions.split(';')
        for (var i = 0; i < enabledVersions.length; i++) {
          let ver = enabledVersions[i]
          if (ver === version) {
            if (!this.enableInCatalog[ver]) {
              versions.push(ver)
            }
          } else {
            if (this.enableInCatalog[ver]) {
              versions.push(ver)
            }
          }
        }
        this.page.enable_catalog = versions
      }
    },
    getSaveData () {
      const exportFields = [
        'id',
        'name',
        'description',
        'file_id',
        'file_ext',
        'file_title',
        'file_description',
        'file_video',
        'enable_catalog',
        'sharedFile',
        'sharedSaleschannel'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.page[e], a), {})
      data.version = this.version
      return data
    },
    async save () {
      await this.awaitUploadsBeforeSave()
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/pages/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/pages.json?apikey=' + this.apiKey, data)
        }
      } catch(e) {
        this.saving = false
        this.applyPendingUploads()
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadPage(response.data.id)
      }
      this.saving = false
      this.applyPendingUploads()
      this.saved = true
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.page)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    removeImage(image) {
      if (image === 'main') { // Remove main image
        this.page.file_id = null
        this.page.file_ext = null
      } else {
        this.page.sharedFile = this.page.sharedFile.filter(i => i.id !== image.id)
      }
    },
    // Puts a file uploaded through the upload queue in its place (lib/uploadaware.js)
    applyUploadResult (slot, file) {
      if (!this.page || !file) {
        return
      }
      if (slot === 'main') {
        this.page.file_ext = file.ext
        this.page.file_id = this.page.image_id = file.id
        return
      }
      if (!Array.isArray(this.page.sharedFile)) {
        this.page.sharedFile = []
      }
      if (this.page.sharedFile.findIndex(i => String(i.id) === String(file.id)) === -1) {
        this.page.sharedFile.push({ id: file.id })
      }
    },
    editCaption(source) {
      let data = {}
      if (source === 'main') {
        data.defaultTitle = this.page.file_title
        data.defaultDescription = this.page.file_description
        data.defaultVideo = this.page.file_video
        data.source = 'main'
      } else {
        data.defaultTitle = source.title
        data.defaultDescription = source.description
        data.defaultVideo = source.video
        data.source = source
      }
      data.settings = this.settings
      this.showModal('filecaption', FileCaption, {
        props: data,
        events: {
          save: this.saveCaption
        },
        handlers: {},
        width: 800
      })
    },
    saveCaption (data) {
      if (data.source === 'main') {
        this.page.file_title = data.title
        this.page.file_description = data.description
        this.page.file_video = data.video
      } else {
        for (let i = 0; i < this.page.sharedFile.length; i++) {
          if (this.page.sharedFile[i].id === data.source.id) {
            this.page.sharedFile[i].title = data.title
            this.page.sharedFile[i].description = data.description
            this.page.sharedFile[i].video = data.video
            break
          }
        }
      }
    },
  },
  components: {
    Editor
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadPage(this.component.initData.pageId)
    }
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.page && this.page.id && parseInt(this.page.id, 10) !== parseInt(this.component.initData.pageId, 10)) {
        try {
          await this.loadPage(this.component.initData.pageId)
        } catch(e) {}
      }
    }
  },
  async mounted () {
    if(!this.events) {
      let events = {}
      events['pageUpdate'] = async (message) => {
        if (message && message.data && this.page && parseInt(message.data.id, 10) === parseInt(this.page.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadPage(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>

<style scoped>
ol#additionalImages {
  list-style-type: none;
  padding: 0;
}
</style>